import {
  ResponsiveContainer,
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";
import { scaleQuantize } from "d3-scale";
import { geoCentroid } from "d3-geo";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  Marker,
} from "react-simple-maps";
import MuiTooltip from "@mui/material/Tooltip";
import rwandaDistricts from "../../../../../utils/rwanda-districts.json";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import GroupsIcon from "@mui/icons-material/Groups";

export default function DistrictVacancyDistributionChart({ data, summary }) {
  const type = data.length > 0 ? data[0].type : undefined;
  const sortedData = [...data].sort((a, b) => b.count - a.count);

  const colorScale = scaleQuantize()
    .domain([
      Math.min(...data.map((d) => d.count)),
      Math.max(...data.map((d) => d.count)),
    ])
    .range([
      "#f7fbff",
      "#deebf7",
      "#c6dbef",
      "#9ecae1",
      "#6baed6",
      "#4292c6",
      "#2171b5",
      "#084594",
    ]);

  const getDistrictData = (districtName) => {
    const district = data.find((d) => d.name === districtName);
    return district ? district.count : 0;
  };

  const minCount = Math.min(...data.map((d) => d.count));
  const maxCount = Math.max(...data.map((d) => d.count));
  
  const minDistrict = data.find((d) => d.count === minCount);
  const maxDistrict = data.find((d) => d.count === maxCount);

  const VacancySummary = () => {
    const totalVacantPosts = summary?.find(
      (item) => item.vacancyType === "Total Vacant Posts"
    ) || { total: 2234 };
    const teachingVacantPosts = summary?.find(
      (item) => item.vacancyType === "Teaching Vacant Posts"
    ) || { total: 1112 };
    const administrativeVacantPosts = {
      total: totalVacantPosts.total - teachingVacantPosts.total
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          padding: "10px",
          background: "#f8f9fa",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ display: "flex", gap: "10px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "8px",
              background: "rgba(49, 104, 197, 0.05)",
              borderRadius: "6px",
              paddingRight: "16px",
            }}
          >
            <SchoolIcon
              style={{
                fontSize: 32,
                color: "#3168c5",
              }}
            />
            <div>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "25px",
                  color: "#3168c5",
                  lineHeight: "1.2",
                }}
              >
                {teachingVacantPosts.total.toLocaleString()}
              </div>
              <span
                style={{
                  fontSize: "15px",
                  color: "#3168c5",
                  lineHeight: "1.2",
                }}
              >
                Teaching vacancies
              </span>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "8px",
              background: "rgba(255, 152, 0, 0.05)",
              borderRadius: "6px",
              paddingRight: "16px",
            }}
          >
            <WorkIcon
              style={{
                fontSize: 32,
                color: "hsl(var(--chart-2))",
              }}
            />
            <div>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "25px",
                  color: "hsl(var(--chart-2))",
                  lineHeight: "1.2",
                }}
              >
                {administrativeVacantPosts.total.toLocaleString()}
              </div>
              <span
                style={{
                  fontSize: "15px",
                  color: "hsl(var(--chart-2))",
                  lineHeight: "1.2",
                }}
              >
                Administrative vacancies
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "13px",
              background: "linear-gradient(135deg, rgba(49, 104, 197, 0.1) 50%, rgba(255, 152, 0, 0.1) 50%)",
              borderRadius: "6px",
              paddingRight: "16px",
              minWidth: "280px",
            }}
          >
            <GroupsIcon
              style={{
                fontSize: 32,
                background: "linear-gradient(135deg, #3168c5 50%, hsl(var(--chart-2)) 50%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            />
            <div>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "25px",
                  background: "linear-gradient(135deg, #3168c5 100%, hsl(var(--chart-2)) 0%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  lineHeight: "1.2",
                }}
              >
                {totalVacantPosts.total.toLocaleString()}
              </div>
              <span
                style={{
                  fontSize: "15px",
                  background: "linear-gradient(135deg, #3168c5 10%, hsl(var(--chart-2)) 90%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  lineHeight: "1.2",
                  display: "block",
                }}
              >
                Total vacant posts
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (type !== "DISTRICT") {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          justifyContent: "center",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <ResponsiveContainer width="100%" height={442}>
          <BarChart
            data={sortedData}
            layout="vertical"
            margin={{ right: 30, left: -30, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis
              type="category"
              dataKey="name"
              width={150}
              orientation="left"
              tick={{ textAnchor: "start", dx: -90 }}
              interval={0}
              style={{ fontSize: 14 }}
            />
            <Tooltip
              formatter={(value) => [
                `${value?.toLocaleString()} ${
                  value === 1 ? "Vacancy" : "Vacancies"
                }`,
                "Count",
              ]}
              labelFormatter={(name) => `${name}`}
            />
            <Legend />
            <Bar
              dataKey="count"
              fill="hsl(var(--chart-1))"
              name="Vacancies"
              barSize={type === "SECTOR" ? 15 : 50}
            >
              <LabelList
                dataKey="count"
                position="right"
                formatter={(value) => value.toLocaleString()}
                style={{
                  fontWeight: "bold",
                  fontSize: 14,
                  fill: "black",
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div style={{ display: "flex", alignItems: "center" }}>
          <VacancySummary />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        height: "630px",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "20px",
      }}
    >
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 19500,
          center: [29.9, -1.94],
        }}
        style={{ width: "100%", height: "100%" }}
      >
        <ZoomableGroup>
          <Geographies geography={rwandaDistricts}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const districtName = geo.properties.district;
                const count = getDistrictData(districtName);
                const centroid = geoCentroid(geo);
                const vacancyText = `${parseInt(count)?.toLocaleString()} ${
                  count === 1 ? "vacancy" : "vacancies"
                }`;
                return (
                  <MuiTooltip
                    key={geo.rsmKey}
                    title={
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: "#ffffff",
                          padding: "8px",
                          borderRadius: "4px",
                        }}
                      >
                        {districtName}: {vacancyText}
                      </div>
                    }
                    arrow
                    placement="top"
                  >
                    <g>
                      <Geography
                        geography={geo}
                        stroke="#FFFFFF"
                        style={{
                          default: {
                            fill: colorScale(count),
                            outline: "none",
                          },
                          hover: {
                            fill: "#fdae61",
                            outline: "none",
                            cursor: "pointer",
                          },
                          pressed: {
                            fill: "#d73027",
                            outline: "none",
                          },
                        }}
                      />
                      <Marker coordinates={centroid}>
                        <text
                          textAnchor="middle"
                          alignmentBaseline="central"
                          style={{
                            fontSize: 16,
                            fill: "#000",
                            fontWeight: "bold",
                          }}
                        >
                          {parseInt(count)?.toLocaleString()}
                        </text>
                      </Marker>
                    </g>
                  </MuiTooltip>
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
      <div className="d-flex align-items-center" style={{ gap: "8px" }}>
        <div className="d-flex flex-column align-items-center justify-content-center">
          <span style={{ fontSize: "11px" }}>
            {minDistrict?.name || "Min"}
          </span>
          <div style={{ fontSize: "14px", fontWeight: "bold" }}>
            {minCount.toLocaleString()}
          </div>
        </div>
        <div className="d-flex align-items-center" style={{ gap: "2px" }}>
          {colorScale.range().map((color, index) => (
            <div
              key={index}
              style={{
                width: "20px",
                height: "20px",
                backgroundColor: color,
                margin: "2px",
              }}
            ></div>
          ))}
        </div>
        <div className="d-flex flex-column align-items-center justify-content-center">
          <span style={{ fontSize: "11px" }}>
            {maxDistrict?.name || "Max"}
          </span>
          <div style={{ fontSize: "14px", fontWeight: "bold" }}>
            {maxCount.toLocaleString()}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center" }}>
        <VacancySummary />
      </div>
    </div>
  );
}
